/**
 * The page: one socket mirroring the server's Grid and Walker, one audio core
 * sounding the walk, one canvas drawing it, and the Brushes painting into it.
 * The timing arithmetic is in timing.ts, the Path bookkeeping in path.ts, the
 * optimistic overlay in overlay.ts, the stroke geometry in paint.ts, the
 * Odds glow and trail in reach.ts and the cursor pacing in cursor.ts, all pure
 * and tested; this file is the wiring.
 */
import type { Report, WalkerAudio } from "./audio.ts"
import { startAudio } from "./audio.ts"
import type { Brush } from "./brush.ts"
import { BRUSHES, brushForKey } from "./brush.ts"
import type { CursorOut } from "./cursor.ts"
import { cursorOut, forgotten, othersOf, pointAt, take } from "./cursor.ts"
import type { Hit, Mood as Face } from "./grid.ts"
import { CELL_PX, FLASH_MS, GRID_PX, makeGridCanvas, MARGIN_PX } from "./grid.ts"
import type { Overlay } from "./overlay.ts"
import { applyEcho, applyPaint, drawnGrid, dropExpired, emptyOverlay } from "./overlay.ts"
import { fit } from "./fit.ts"
import { maskOf, spanFrames } from "./fuckery.ts"
import type { Groove } from "./groove.ts"
import { groove, swingMs } from "./groove.ts"
import { cellOf, changesFor, spotAt, strokeCells } from "./paint.ts"
import type { Spot } from "./paint.ts"
import { SPLASH_ICONS } from "./icons.ts"
import type { Move, Path, PathEntry, Reported } from "./path.ts"
import {
  drainMoves,
  emptyPath,
  entryAt,
  forgetReported,
  pendingEntries,
  pendingOn,
  receivePath,
  retime,
  timerMoves
} from "./path.ts"
import { makeBrushRail, makeSlider, makeSwitch } from "./rail.ts"
import type { Upcoming } from "./reach.ts"
import { emptyUpcoming, holdOdds, landOn, leave, nextOdds } from "./reach.ts"
import type { Slider } from "./slider.ts"
import { drag, expireHold, hearCtl, release, resync, shown, sliderAt } from "./slider.ts"
import { openSocket } from "./socket.ts"
import { handOf, soundFor, velocityFor } from "./sound.ts"
import type { Mapping, OffsetSample, Pairing } from "./timing.ts"
import {
  adopt,
  bestOffset,
  bestPairing,
  cueFrame,
  localDue,
  OFFSET_SAMPLES,
  offsetSample,
  pageTimeOf,
  PAIRING_WINDOW_MS
} from "./timing.ts"
import type { CursorSet, Downstream, Mood, WalkMessage } from "./wire.ts"
import { MOODS } from "./wire.ts"
import { cellValue, changedEvents, EMPTY, eventAt, paintCells, WALL } from "./world.ts"

const find = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector)
  if (element === null) throw new Error(`missing ${selector}`)
  return element
}

const splash = find<HTMLElement>("[data-splash]")
const splashIcon = find<HTMLElement>("[data-splash-icon]")
const canvas = makeGridCanvas(find<HTMLCanvasElement>("[data-grid]"))

// The Grid and the Walker as this page mirrors them. `drawn` is the Grid the
// page draws and sounds: the server's, with this page's own unconfirmed paint
// over it. `you` is this connection's id, which tells an own echo from theirs.
let grid = ""
let overlay: Overlay = emptyOverlay
let drawn = ""
let you = 0
let path: Path = emptyPath
let walk: WalkMessage | undefined

// The clocks. The pairing in use outlives its samples: a hidden tab reads no
// pairs while the Walker stands, and the next report re-pairs.
let offsetSamples: ReadonlyArray<OffsetSample> = []
// Chronological, pruned from the front, so a frame's read is a push and no copy.
const pairs: Array<Pairing> = []
let mapping: Mapping | undefined
let pairingInUse: Pairing | undefined

// The Walker's dot. `heard` means it follows entries, and a `walk` message
// never moves a dot that follows (ADR 0003). `arrived` is the last Step it
// landed on, from a report or from a timer, and the floor for re-cueing.
let moves: ReadonlyArray<Move> = []
let arrived: Reported | undefined
let dot: number | undefined
let heard = false
let hit: Hit | undefined

// What the Walker has done and what it may do next: the Cells it has left, and
// the Odds held for Steps not yet heard, which glow for the Step before theirs.
let trail: ReadonlyArray<number> = []
let upcoming: Upcoming = emptyUpcoming

// Other hands. `others` is the server's cursor set with this page's own left out.
// `pointer` is the Cell under this page's own pointer, -1 off the Grid, drawn
// at once and sent as `cursor`, paced by `cursor`. `brackets` is what the
// canvas draws of the others, a new array only when one of them moves.
// `flashes` are freshly painted Cells.
let others: CursorSet = {}
let pointer = -1
let cursor: CursorOut = cursorOut
let brackets: ReadonlyArray<number> = []
const flashes = new Map<number, number>()

const showCursors = () => {
  brackets = Object.values(others)
}

const flash = (cells: ReadonlyArray<readonly [number, number]>, now: number) => {
  for (const [cell] of cells) flashes.set(cell, now)
}

let audio: WalkerAudio | undefined
let joined = false
let audioStopped = false
let hourUp = false

/**
 * The page has no status line: the Walker's face is the status. Awake while
 * it walks, asleep while it stands with nobody here, dozing off z's while it
 * rests out the day's budget, and grey once the sound has stopped for good.
 */
const face = (): Face => {
  if (audioStopped || hourUp) return "off"
  switch (walk?.state) {
    case "standing":
      return "standing"
    case "resting":
      return "resting"
    default:
      return "awake"
  }
}

/**
 * The splash, in one of its four pictures: join, the sound is paused (the
 * phone took it away: a tap resumes it), the hour is up, the sound has stopped.
 */
const showSplash = (state: "join" | "paused" | "hour" | "stopped") => {
  splash.dataset["state"] = state
  splashIcon.innerHTML = SPLASH_ICONS[state] ?? ""
  splash.setAttribute(
    "aria-label",
    state === "join"
      ? "join and hear the walk"
      : state === "paused"
        ? "the sound paused: tap to resume"
        : state === "hour"
          ? "your hour is up: reload"
          : "the sound stopped: reload"
  )
  splash.hidden = false
}
showSplash("join")

// The walk's Drift, one per gen: every Visitor derives the same from it.
let drift: Groove | undefined

const cue = (entry: PathEntry) => {
  if (audio === undefined || mapping === undefined || mapping.pairing === undefined) return
  // Read afresh on every re-cue: a paint may have changed the sound.
  const event = soundFor(eventAt(drawn, entry.cell), entry.count)
  if (drift?.gen !== entry.gen) drift = groove(entry.gen)
  const hand = handOf(event)
  // A rest has no hand and is cued on the grid.
  const drifted = hand === undefined ? { ms: 0, gain: 1 } : drift.at(entry.step, hand)
  audio.cue({
    gen: entry.gen,
    step: entry.step,
    // The server keeps the grid; the page swings the e and the a off it, and
    // each hand drifts about it.
    frame: cueFrame(
      localDue(entry.due, mapping.offset) + swingMs(entry.count, swing.shared, tempo.shared) + drifted.ms,
      mapping.pairing,
      audio.context.sampleRate
    ),
    event,
    velocity: velocityFor(event) * drifted.gain,
    forms: maskOf(entry.forms),
    // The hit's repeats fill its Step at the room's tempo.
    span: spanFrames(tempo.shared, audio.context.sampleRate)
  })
}

/**
 * Write the drawn Grid again and re-cue every pending Step whose Cell has come
 * to sound differently, by anyone's paint, this page's own optimistic one, an
 * echo or an expiry. The page sounds the Grid it draws, so a Cell painted
 * before its Step is due sounds what it now holds.
 */
const redrawGrid = () => {
  const before = drawn
  drawn = drawnGrid(grid, overlay)
  for (const entry of pendingOn(path, arrived, changedEvents(before, drawn))) cue(entry)
}

/** Behind the splash every pending entry gets a timer; once audio runs, a Cue. */
const follow = (entries: ReadonlyArray<PathEntry>) => {
  if (mapping === undefined || entries.length === 0) return
  heard = true
  if (mapping.pairing === undefined) moves = retime(moves, timerMoves(entries, mapping.offset))
  else for (const entry of entries) cue(entry)
}

/** A new mapping re-times or re-cues everything the dot has not yet arrived on. */
const refollow = () => {
  if (mapping?.pairing === undefined) moves = []
  follow(pendingEntries(path, arrived))
}

const readPair = () => {
  // A context that is not running has a frozen clock, and a pair read from it lies.
  if (audio === undefined || audio.context.state !== "running") return
  const audioTime = audio.context.currentTime
  const page = performance.now()
  pairs.push({ audio: audioTime, page })
  while (pairs.length > 0 && pairs[0]!.page < page - PAIRING_WINDOW_MS) pairs.shift()
}

/** Recompute the best estimate and adopt it if it moves the mapping by more than a quantum. */
const reconsider = () => {
  const offset = bestOffset(offsetSamples)
  if (offset === undefined) return
  const pairing = bestPairing(pairs, performance.now()) ?? pairingInUse
  const next = adopt(mapping, { offset, pairing })
  if (next === mapping) return
  mapping = next
  pairingInUse = next.pairing
  refollow()
}

/** The dot moves on to a Cell, and the Cell it left joins the trail. */
const moveOn = (cell: number) => {
  if (dot !== undefined) trail = leave(trail, dot)
  dot = cell
}

const land = (move: Move) => {
  moveOn(move.cell)
  // Every sounded Step punches; a missed Cue sounded nothing.
  if (move.missed !== true) {
    hit = { cell: move.cell, at: move.at, forms: move.forms }
    // Its Brush's key flashes with it, over two Steps: 30,000 / bpm ms. A rest and a Wall have no Event to flash.
    const value = cellValue(drawn, move.cell)
    if (value !== EMPTY && value !== WALL) rail.strike(value, 30_000 / tempo.shared)
  }
  upcoming = landOn(upcoming, move.gen, move.step)
  // A timer landing is the Onlooker's report: the click never cues what has passed.
  if (mapping?.pairing === undefined) {
    arrived = { gen: move.gen, step: move.step }
    path = forgetReported(path, arrived)
  }
}

const applyPath = (entries: ReadonlyArray<PathEntry>) => {
  const before = path.gen
  const received = receivePath(path, arrived, entries)
  path = received.path
  upcoming = holdOdds(upcoming, entries)
  // A new generation discards everything pending, timers included.
  if (path.gen !== before) moves = []
  follow(received.cue)
}

// The shared sliders. The hand wins while it drags; each frame sends the latest
// value it reached, if it moved, as one `set` per knob.
let tempo: Slider = sliderAt(155)
let temperature: Slider = sliderAt(0.2)
let swing: Slider = sliderAt(54)
// The Fuckery switch holds like a slider, by its position in `MOODS`; a press is a drag let go at once.
let mood: Slider = sliderAt(MOODS.indexOf("loose"))
let outgoingBpm: number | undefined
let outgoingTemp: number | undefined
let outgoingSwing: number | undefined
let outgoingMood: Mood | undefined

// All four knobs in one column to the Grid's right (a row below it in portrait), the groove's
// before the Walker's attitude.
const knobs = find<HTMLElement>("[data-knobs]")
const showSliders = () => {
  tempoSlider.show(shown(tempo))
  temperatureSlider.show(shown(temperature))
  swingSlider.show(shown(swing))
  moodSwitch.show(shown(mood))
}
const tempoSlider = makeSlider(
  knobs,
  { name: "tempo", min: 100, max: 180, step: 1, format: (bpm) => String(bpm) },
  {
    onDrag: (bpm) => {
      tempo = drag(tempo, bpm)
      outgoingBpm = bpm
      showSliders()
    },
    onRelease: () => {
      tempo = release(tempo, performance.now())
    }
  }
)
const swingSlider = makeSlider(
  knobs,
  { name: "swing", min: 50, max: 75, step: 1, format: (percent) => String(percent) },
  {
    onDrag: (percent) => {
      swing = drag(swing, percent)
      outgoingSwing = percent
      showSliders()
    },
    onRelease: () => {
      swing = release(swing, performance.now())
    }
  }
)
const temperatureSlider = makeSlider(
  knobs,
  { name: "temp", min: 0, max: 1, step: 0.01, format: (temp) => temp.toFixed(2).replace(/^0/, "") },
  {
    onDrag: (temp) => {
      temperature = drag(temperature, temp)
      outgoingTemp = temp
      showSliders()
    },
    onRelease: () => {
      temperature = release(temperature, performance.now())
    }
  }
)
const moodSwitch = makeSwitch(knobs, { name: "fuckery", positions: MOODS }, (index) => {
  mood = release(drag(mood, index), performance.now())
  outgoingMood = MOODS[index]
  showSliders()
})

showSliders()

const onMessage = (message: Downstream) => {
  switch (message.t) {
    case "snapshot":
      // A snapshot replaces everything, every unconfirmed overlay with it.
      you = message.you
      grid = message.grid
      overlay = emptyOverlay
      drawn = grid
      path = emptyPath
      moves = []
      trail = []
      upcoming = emptyUpcoming
      // The cursor set follows the snapshot when there is one; a flash is a moment, not state.
      others = {}
      flashes.clear()
      showCursors()
      walk = message.walk
      tempo = resync(tempo, message.ctl.bpm)
      temperature = resync(temperature, message.ctl.temp)
      swing = resync(swing, message.ctl.swing)
      mood = resync(mood, MOODS.indexOf(message.ctl.mood))
      showSliders()
      // A fresh open while Standing or Resting: the one case with no entry to follow.
      heard = message.path.length > 0
      applyPath(message.path)
      if (!heard) dot = message.walk.cell
      return
    case "cell":
      grid = paintCells(grid, message.cells)
      // Your own echo confirms what it carries; anyone else's write wins outright.
      overlay = applyEcho(overlay, message.cells, message.by === you)
      // Your own paint flashed as you made it; everyone else's flashes as it lands.
      if (message.by !== you) flash(message.cells, performance.now())
      redrawGrid()
      return
    case "cursors":
      others = othersOf(message.c, you)
      showCursors()
      return
    case "ctl": {
      // All four knobs every time; the one someone else changed flashes.
      const own = message.by === you
      const heardTempo = hearCtl(tempo, message.bpm, own)
      const heardTemperature = hearCtl(temperature, message.temp, own)
      const heardSwing = hearCtl(swing, message.swing, own)
      const heardMood = hearCtl(mood, MOODS.indexOf(message.mood), own)
      const swung = heardSwing.slider.shared !== swing.shared
      tempo = heardTempo.slider
      temperature = heardTemperature.slider
      swing = heardSwing.slider
      mood = heardMood.slider
      if (heardTempo.flash) tempoSlider.flash()
      if (heardTemperature.flash) temperatureSlider.flash()
      if (heardSwing.flash) swingSlider.flash()
      if (heardMood.flash) moodSwitch.flash()
      showSliders()
      // What is pending is cued again at the room's new swing; a re-cue of a Step overwrites it.
      if (swung) refollow()
      return
    }
    case "path":
      applyPath(message.entries)
      return
    case "walk":
      walk = message
      if (!heard) dot = message.cell
      return
    case "pong":
      return
  }
}

const socket = openSocket({
  onOpen: () => {
    // The server's clock may be a new process's: nothing estimated before this open is trusted.
    offsetSamples = []
    mapping = undefined
    // A new socket is a new id, with no cursor held for it.
    cursor = forgotten(cursor)
    if (joined) {
      socket.send({ t: "join" })
      socket.send({ t: "present", v: !document.hidden })
    }
  },
  onMessage,
  onPong: (aSent, aRecv, s) => {
    offsetSamples = [...offsetSamples.slice(-(OFFSET_SAMPLES - 1)), offsetSample({ aSent, aRecv, s })]
    reconsider()
  },
  onHourUp: () => {
    hourUp = true
    showSplash("hour")
  }
})

document.addEventListener("visibilitychange", () => {
  const visible = !document.hidden
  socket.setVisible(visible)
  if (!joined) return
  // A hidden tab has no animation frames to pace it, and no pointer to show.
  if (!visible) {
    pointer = -1
    cursor = forgotten(pointAt(cursor, -1))
    showCursors()
    socket.send({ t: "cursor", c: -1 })
  }
  socket.send({ t: "present", v: visible })
  if (visible) tryResume()
})
// Back from the lock screen or the back-forward cache, the sound may be let back without a tap.
window.addEventListener("pageshow", () => {
  if (joined) tryResume()
})

/** Ask for the sound back. Outside a gesture iOS may refuse, and the paused splash waits for a tap. */
const tryResume = () => {
  if (audio === undefined || audio.context.state === "running" || audio.context.state === "closed") return
  audio.context.resume().catch((error: unknown) => {
    console.warn("audio resume refused", error)
  })
}

/**
 * The phone can take the sound away: a lock screen, an alarm, a call. The
 * context leaves `running` (iOS says `interrupted` or `suspended`) and no
 * report comes, so nothing plays and the dot stands. The splash says so, and
 * its tap brings the same context back.
 */
const onAudioState = () => {
  if (audio === undefined || audioStopped || hourUp) return
  switch (audio.context.state) {
    case "running":
      // The clock stood still for the pause: pair afresh, and re-cue what is pending by it.
      pairs.length = 0
      readPair()
      reconsider()
      if (splash.dataset["state"] === "paused") splash.hidden = true
      return
    case "closed":
      audioStopped = true
      showSplash("stopped")
      return
    default:
      showSplash("paused")
  }
}

/** The core's report: the page's only clock for the Walker's position once audio runs. */
const onReport = (report: Report) => {
  // A hidden tab has no animation frames, so the report is where it re-pairs.
  readPair()
  reconsider()
  const entry = entryAt(path, report.gen, report.step)
  arrived = { gen: report.gen, step: report.step }
  path = forgetReported(path, arrived)
  if (entry === undefined || audio === undefined || mapping?.pairing === undefined) return
  const at = pageTimeOf(report.time + (audio.context.outputLatency || 0), mapping.pairing)
  const move: Move = {
    at,
    cell: entry.cell,
    gen: report.gen,
    step: report.step,
    ...(entry.forms === undefined ? {} : { forms: entry.forms })
  }
  moves = [...moves, report.missed ? { ...move, missed: true } : move]
  heard = true
}

const join = async () => {
  document.removeEventListener("pointerdown", onSplashClick)
  try {
    audio = await startAudio({
      onReport,
      onError: () => {
        audioStopped = true
        showSplash("stopped")
      }
    })
  } catch (error) {
    console.error("audio failed to start", error)
    document.addEventListener("pointerdown", onSplashClick)
    return
  }
  audio.context.addEventListener("statechange", onAudioState)
  // The first pairing, taken at the click: the timers go and every pending entry is cued.
  readPair()
  moves = []
  reconsider()
  joined = true
  // An Onlooker's cursor was never held, so the pointer is said again from here.
  cursor = forgotten(cursor)
  showCursors()
  tempoSlider.enable()
  temperatureSlider.enable()
  swingSlider.enable()
  moodSwitch.enable()
  socket.send({ t: "join" })
  socket.send({ t: "present", v: !document.hidden })
  splash.hidden = true
}

const onSplashClick = () => {
  void join()
}
document.addEventListener("pointerdown", onSplashClick)
// Once joined, the splash comes back to say the sound is paused, where a tap
// resumes it, or that the session is over, where a click reloads.
splash.addEventListener("click", () => {
  const state = splash.dataset["state"]
  if (state === "paused") tryResume()
  else if (state !== "join") location.reload()
})

// Painting. The chosen Brush, the stroke in progress, and this frame's changes,
// one per Cell with the last write winning, which go up as one `paint` message.
let brush: Brush = BRUSHES[0]!
let stroke: Spot | undefined
const outgoing = new Map<number, number>()

const keysColumn = find<HTMLElement>("[data-brushes]")
const rail = makeBrushRail(keysColumn, (picked) => {
  brush = picked
})
rail.show(brush)

// The deck fits the window and never grows: each column (a row in portrait) shrinks to the room
// along it, and the Grid to what they leave, its margin covered first, down to one Cell. Zoom,
// not a transform, so nothing overflows for a phone to zoom out to.
const fitDeck = () => {
  const parts = [keysColumn, knobs, canvas.element]
  for (const part of parts) part.style.zoom = ""
  canvas.element.style.margin = ""
  const body = getComputedStyle(document.body)
  const gutter = 16
  const zooms = fit(
    {
      width: document.documentElement.clientWidth - gutter,
      height: document.documentElement.clientHeight - parseFloat(body.paddingBottom) - gutter
    },
    {
      keys: { width: keysColumn.offsetWidth, height: keysColumn.offsetHeight },
      knobs: { width: knobs.offsetWidth, height: knobs.offsetHeight },
      canvas: canvas.element.offsetWidth,
      margin: MARGIN_PX,
      keep: CELL_PX
    },
    portrait.matches
  )
  keysColumn.style.zoom = String(zooms.keys)
  knobs.style.zoom = String(zooms.knobs)
  canvas.element.style.zoom = String(zooms.grid)
  canvas.element.style.margin = `${-zooms.eat}px`
}
const portrait = window.matchMedia("(orientation: portrait)")
window.addEventListener("resize", fitDeck)
fitDeck()

/**
 * Paint these Cells with the chosen Brush. A Cell that already holds the
 * Brush's value is left alone, so a tap never cycles and only the eraser
 * erases. The paint shows at once as an overlay and sounds at once with it.
 */
const paint = (cells: ReadonlyArray<number>) => {
  // An Onlooker cannot paint, and nothing local ever reaches the world uninvited.
  if (!joined) return
  const changes = changesFor(drawn, cells, brush.value)
  if (changes.length === 0) return
  const now = performance.now()
  overlay = applyPaint(overlay, changes, now)
  flash(changes, now)
  for (const [cell, value] of changes) outgoing.set(cell, value)
  redrawGrid()
}

const spotOf = (event: PointerEvent): Spot => {
  const box = canvas.element.getBoundingClientRect()
  // The Grid may be zoomed down to fit: the box is on screen, the Cells are in the canvas's own px,
  // counted from inside the open margin round the Grid.
  const scale = box.width / (GRID_PX + MARGIN_PX * 2)
  return spotAt(
    (event.clientX - box.left) / scale - MARGIN_PX,
    (event.clientY - box.top) / scale - MARGIN_PX,
    CELL_PX
  )
}

canvas.element.addEventListener("pointerdown", (event) => {
  if (!joined) return
  // Pointer capture keeps the stroke alive off the Grid, and makes the release
  // land here wherever the pointer has got to.
  canvas.element.setPointerCapture(event.pointerId)
  stroke = spotOf(event)
  const cell = cellOf(stroke)
  if (cell !== undefined) paint([cell])
})

/** The pointer is over this Cell now, or -1 off the Grid; the crosshair follows at once. */
const hover = (cell: number) => {
  if (cell === pointer) return
  pointer = cell
  cursor = pointAt(cursor, cell)
  showCursors()
}

canvas.element.addEventListener("pointermove", (event) => {
  const spot = spotOf(event)
  hover(cellOf(spot) ?? -1)
  if (stroke === undefined) return
  paint(strokeCells(stroke, spot))
  stroke = spot
})
canvas.element.addEventListener("pointerleave", () => {
  hover(-1)
})

const endStroke = () => {
  stroke = undefined
}
canvas.element.addEventListener("pointerup", endStroke)
canvas.element.addEventListener("pointercancel", endStroke)
// The backstop: capture is lost on every end of a stroke, wherever it ended.
canvas.element.addEventListener("lostpointercapture", endStroke)

// Keys 1 to 6 pick a Brush, the same six the rail shows.
document.addEventListener("keydown", (event) => {
  const picked = brushForKey(event.key)
  if (picked === undefined) return
  brush = picked
  rail.show(picked)
})

const drain = (now: number) => {
  const drained = drainMoves(moves, now)
  moves = drained.pending
  // Steps passed over in one drain were heard all the same: the trail keeps them.
  for (const passed of drained.passed) moveOn(passed.cell)
  if (drained.landed !== undefined) land(drained.landed)
}

const frame = () => {
  const now = performance.now()
  readPair()
  reconsider()
  drain(now)
  // Nothing unconfirmed stays on screen longer than 2 s; a revert is all the
  // feedback a paint the server dropped needs.
  const held = dropExpired(overlay, now)
  if (held !== overlay) {
    overlay = held
    redrawGrid()
  }
  const tempoHeld = expireHold(tempo, now)
  const temperatureHeld = expireHold(temperature, now)
  const swingHeld = expireHold(swing, now)
  const moodHeld = expireHold(mood, now)
  if (tempoHeld !== tempo || temperatureHeld !== temperature || swingHeld !== swing || moodHeld !== mood) {
    tempo = tempoHeld
    temperature = temperatureHeld
    swing = swingHeld
    mood = moodHeld
    showSliders()
  }
  if (outgoingBpm !== undefined) socket.send({ t: "set", bpm: outgoingBpm })
  if (outgoingTemp !== undefined) socket.send({ t: "set", temp: outgoingTemp })
  if (outgoingSwing !== undefined) socket.send({ t: "set", swing: outgoingSwing })
  if (outgoingMood !== undefined) socket.send({ t: "set", mood: outgoingMood })
  outgoingBpm = undefined
  outgoingTemp = undefined
  outgoingSwing = undefined
  outgoingMood = undefined
  if (outgoing.size > 0) {
    socket.send({ t: "paint", cells: [...outgoing].map(([cell, value]) => [cell, value] as const) })
    outgoing.clear()
  }
  if (joined) {
    const taken = take(cursor, now)
    cursor = taken.out
    if (taken.send !== undefined) socket.send({ t: "cursor", c: taken.send })
  }
  canvas.draw(
    {
      grid: drawn,
      walker: dot,
      hit,
      odds: nextOdds(upcoming),
      trail,
      others: brackets,
      // Only a Visitor has a cursor, here as everywhere else.
      pointer: joined ? pointer : -1,
      brush: brush.value,
      flashes,
      stepMs: 15_000 / tempo.shared,
      mood: face()
    },
    now
  )
  for (const [cell, at] of flashes) if (now - at >= FLASH_MS) flashes.delete(cell)
  requestAnimationFrame(frame)
}
requestAnimationFrame(frame)

// A hidden tab has no animation frames; its dot still lands so the return shows the present.
setInterval(() => {
  if (document.hidden) drain(performance.now())
}, 1000)
